<script setup lang="ts">
// A status shown as a labelled, coloured tag rather than a bare icon: support could not tell the
// states apart from the icon alone, and an unknown status used to render an empty cell. The labels
// reuse the shared `status.*` keys the trainer app already uses, so both apps read the same.
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    status: String,
});

const { t, te } = useI18n();

type TagSeverity = "success" | "info" | "warn" | "danger" | "secondary";

// icon + colour per status; the label comes from the shared status.* namespace.
const PRESENTATION: Record<string, { icon: string; severity: TagSeverity }> = {
    CREATED: { icon: "pi pi-user-plus", severity: "secondary" },
    ACTIVATED: { icon: "pi pi-envelope", severity: "info" },
    STARTED: { icon: "pi pi-pencil", severity: "info" },
    COMPLETED: { icon: "pi pi-flag", severity: "warn" },
    CALCULATED: { icon: "pi pi-calculator", severity: "warn" },
    FILLED: { icon: "pi pi-file-import", severity: "warn" },
    NOTIFIED: { icon: "pi pi-bell", severity: "success" },
    GENERATED: { icon: "pi pi-check", severity: "success" },
    DELETED: { icon: "pi pi-trash", severity: "danger" },
    REVOKED: { icon: "pi pi-ban", severity: "danger" },
};

const presentation = computed(() => (props.status ? PRESENTATION[props.status] : undefined));

// Fall back to the raw status so an unrecognised value is still visible instead of an empty cell.
const label = computed(() => {
    if (!props.status) return "";
    const key = `status.${props.status}`;
    return te(key) ? t(key) : props.status;
});
</script>

<template>
    <Tag
        v-if="status"
        :icon="presentation?.icon"
        :severity="presentation?.severity ?? 'secondary'"
        :value="label"
        style="font-weight: normal"
    />
</template>
